import {
  Align,
  Button,
  Container,
  Distribute,
  IconButton,
  Loader,
  SvgClose,
  SvgInfoOutline,
  SvgLaunch,
  Text,
  TextButton,
  Tooltip,
} from '@loomhq/lens';
import classnames from 'classnames';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requestVideoPlayerSeekToTs } from 'actions/videoPlayer';
import {
  populateViewerInsightsViewers,
  selectInsightsView,
} from 'actions/viewerInsights';
import {
  EI_COMPLETION_RATE,
  EI_CTA_CONVERSION,
  EI_HOW_WE_CALCULATE,
} from 'constants/routes';
import { getGraphQLClient } from 'utilities/graphql';

import { selectVideo, selectVideoViews } from 'selectors/videoPlayer';
import {
  selectActiveViewer,
  selectIsActive,
  selectViewers,
} from 'selectors/viewerInsights';

import Details from './details';
import GetInsightsForVideo from './GetInsightsForVideo.gql';
import {
  trackBackToList,
  trackInfoIcon,
  trackInfoLink,
  trackPartialInfoAck,
  trackViewerDetails,
} from './analytics';
import { Reaction, Segment, ViewerInfo } from './components';
import { getHidePartialInfoStored, hidePartialInfo } from './localStorage';
import { getComputedSegments, percentToColor, toPercent } from './utils';

import ErrorBoundary from 'components/ui-components/error-boundary';

import './style.less';

import styles from './styles.module.less';

const ViewerInsightsList = props => {
  const dispatch = useDispatch();
  const gqlClient = React.useMemo(() => getGraphQLClient(), []);
  const slideableContainerRef = React.useRef();
  const containerRef = React.useRef();
  const [showPartialInfo, setShowPartialInfo] = React.useState(
    !getHidePartialInfoStored()
  );

  const saveSelected = view => {
    dispatch(selectInsightsView(view));
    view && trackViewerDetails();
  };

  const onPartialInfoAck = () => {
    setShowPartialInfo(false);
    hidePartialInfo();
    trackPartialInfoAck();
  };

  const onBack = () => {
    const slideableEl = slideableContainerRef?.current;
    const sectionEl = containerRef?.current;

    slideableEl?.classList.remove(styles.shrinked);

    setTimeout(() => {
      document.documentElement.scrollTo({ top: 0 });
      slideableEl?.scrollTo({ left: 0, behavior: 'smooth' });
      sectionEl?.scrollTo({ top: 0 });
    }, 50);

    trackBackToList();

    setTimeout(() => saveSelected(null), 500);
  };

  const {
    id: videoId,
    cta: { enabled: videoHasCta = false } = {},
  } = useSelector(selectVideo);
  const users = useSelector(selectViewers);
  const views = useSelector(selectVideoViews);
  const selected = useSelector(selectActiveViewer);
  const totalViews = parseInt(views.total || 0, 10);

  const withWatchSession =
    users.filter(user => user.hasWatchSession !== false) || [];
  const hasWatchSessions = Boolean(withWatchSession.length);
  const cta = withWatchSession.filter(({ cta }) => cta) || [];
  const trackedViews = withWatchSession.reduce(
    (tot, s) => tot + s.trackedViews,
    0
  );
  const hasPartialData =
    hasWatchSessions && Math.min(trackedViews, totalViews) !== totalViews;
  const partialData = hasPartialData && [trackedViews, totalViews];
  const ctaConversion = (cta.length * 100) / withWatchSession.length;
  const completionRateAvg =
    withWatchSession
      .map(({ percentCompleted }) => percentCompleted)
      .reduce((p, c) => p + c, 0) / withWatchSession.length;

  React.useEffect(() => {
    containerRef.current.scrollTo({ top: 0 });
  }, [selected]);

  React.useEffect(() => {
    if (users.length) {
      return;
    }

    gqlClient
      .query({
        variables: { videoId },
        query: GetInsightsForVideo,
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        dispatch(populateViewerInsightsViewers(data.insightsForVideo.viewers));
      })
      .catch(error => {
        console.error(error);
      });
  }, [dispatch, gqlClient, users.length, videoId]);

  React.useLayoutEffect(() => {
    if (!containerRef?.current) {
      return;
    }

    const sectionEl = containerRef?.current;
    const slideableEl = slideableContainerRef?.current;

    const resizeFn = () => {
      const elBox = sectionEl.parentElement.getBoundingClientRect();
      const targetBox = document
        .querySelector('.add-comment-buttons')
        .getBoundingClientRect();

      window.requestAnimationFrame(
        () =>
          (sectionEl.parentElement.style.height =
            targetBox.bottom - elBox.top + 'px')
      );
    };

    const ro = new ResizeObserver(resizeFn);

    ro.observe(document.scrollingElement);
    ro.observe(sectionEl);
    ro.observe(slideableEl);
    setTimeout(resizeFn, 50);

    return () => {
      sectionEl.parentElement.style.height = 'auto';
      ro.disconnect();
    };
  }, [containerRef]);

  return (
    <section ref={containerRef} className={styles.containerBox}>
      <ErrorBoundary>
        <Distribute alignment="center" isSpread className={styles.sticky}>
          <Text variant="title">Engagement Insights</Text>
          <IconButton icon={<SvgClose />} onClick={props.onExit} />
        </Distribute>
        {hasPartialData && showPartialInfo && (
          <PartialDataBox onClick={onPartialInfoAck} />
        )}
        <div className={styles.slideableContainer} ref={slideableContainerRef}>
          <section className={styles.slideable}>
            <TextButton
              className="mt:medium"
              size="small"
              icon={<SvgLaunch />}
              onClick={() => {
                window.open(EI_HOW_WE_CALCULATE, '_blank').opener = null;
                trackInfoLink();
              }}
            >
              See how we calculate this
            </TextButton>
            <ul
              className={classnames(styles.viewsMetadata, {
                [styles.partial]: !hasPartialData,
              })}
            >
              <TotalViews total={totalViews} />
              <PercentCompleted
                percent={hasWatchSessions && completionRateAvg}
                partialData={partialData}
              />
              {videoHasCta && (
                <CtaConversion
                  percent={hasWatchSessions && ctaConversion}
                  partialData={partialData}
                />
              )}
            </ul>

            <Viewers users={users} setSelected={saveSelected} />
            {!users.length && <Loader />}
          </section>
          {selected && <Details viewer={selected} onBack={onBack} />}
        </div>
      </ErrorBoundary>
    </section>
  );
};

const TotalViews = ({ total }) => (
  <li>
    <Text fontWeight="semiBold">Total video views</Text>
    <Text size="xxlarge" fontWeight="semiBold" color="blue">
      {total}
    </Text>
  </li>
);

const PercentCompleted = ({ percent, partialData }) => (
  <li className={classnames({ [styles.disabled]: !percent })}>
    <Distribute gap="xsmall" direction="column">
      <Text fontWeight="semiBold">Average Completion Rate</Text>
      <PartialDataLabel
        partialData={partialData}
        source="completion rate"
        href={EI_COMPLETION_RATE}
      />
    </Distribute>
    <BigNumber percent={percent} />
  </li>
);

const CtaConversion = ({ percent, partialData }) => (
  <li className={classnames({ [styles.disabled]: !percent })}>
    <Distribute gap="xsmall" direction="column">
      <Text fontWeight="semiBold">Call-to-Action Conversion</Text>
      <PartialDataLabel
        partialData={partialData}
        source="cta conversion"
        href={EI_CTA_CONVERSION}
      />
    </Distribute>
    <BigNumber percent={percent} />
  </li>
);

const InfoIcon = ({ href, source }) => {
  const onClick = () => trackInfoIcon({ source });

  return (
    <Tooltip
      content="Engagement is only tracked after upgrading to Business or Enterprise. Excludes views on our iOS app."
      placement="topCenter"
    >
      <a
        href={href}
        className={styles.infoIcon}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        <SvgInfoOutline />
      </a>
    </Tooltip>
  );
};

const PartialDataLabel = ({ partialData, source, href }) => {
  if (!partialData) {
    return null;
  }

  const [tracked, total] = partialData;

  return (
    <Text size="small" className={styles.flex}>
      Based on {tracked} of {total} views
      <InfoIcon href={href} source={source} />
    </Text>
  );
};

const PartialDataBox = ({ onClick }) => (
  <Container className={styles.partialDataBox}>
    <Text fontWeight="semiBold" className="mb:medium">
      This video includes partially tracked data.
    </Text>
    <Text>
      Engagement is only tracked after upgrading to Business or Enterprise.
      Excludes views on our iOS app.
    </Text>
    <Align alignment="centerRight">
      <Button variant="primary" onClick={onClick}>
        Got it
      </Button>
    </Align>
  </Container>
);

const BigNumber = ({ percent }) => (
  <Text size="xxlarge" fontWeight="semiBold" color={percentToColor(percent)}>
    {percent > 0 ? toPercent(percent) : '--'}
  </Text>
);

const Viewers = ({ users, setSelected }) => (
  <div className={styles.viewers}>
    <Text fontWeight="semiBold" className="mb:medium">
      {users.length} Viewers
    </Text>
    {users.map((viewer, i) => (
      <div
        className={classnames(styles.card)}
        key={`v-${i}`}
        onClick={() => setSelected(viewer)}
      >
        <ViewerInfo user={viewer.user} events={viewer.events} />
      </div>
    ))}
  </div>
);

export const ViewersInsightsTimeline = () => {
  const selectedViewer = useSelector(selectActiveViewer);
  const isActive = useSelector(selectIsActive);

  if (!isActive || !selectedViewer) {
    return null;
  }

  return (
    <div className="viewerInsightsTimeline">
      <ViewersEventsTimeline selectedViewer={selectedViewer} />
      <ViewerTimeline
        duration={selectedViewer.videoDuration}
        plays={selectedViewer.plays}
        skips={selectedViewer.skips}
        replays={selectedViewer.replays}
      />
    </div>
  );
};

export const ViewersEventsTimeline = ({ selectedViewer }) => {
  const dispatch = useDispatch();
  const currentlySelectedViewer = useSelector(selectActiveViewer);

  const playVideoAt = ts => e => {
    e.stopPropagation();

    if (currentlySelectedViewer !== selectedViewer) {
      dispatch(selectInsightsView(selectedViewer));
    }

    dispatch(requestVideoPlayerSeekToTs(ts));
  };

  return (
    <div className="reaction viewer__events-timeline">
      {(selectedViewer.events || []).map((event, i) => (
        <Reaction
          key={`event${i}`}
          {...event}
          duration={selectedViewer.videoDuration}
          onClick={playVideoAt(event.ts)}
        />
      ))}
    </div>
  );
};

export const ViewerTimeline = ({ duration, plays, skips, replays = [] }) => {
  const dispatch = useDispatch();

  if (!plays) {
    return null;
  }

  const playVideoAt = ts => e => {
    e.stopPropagation();
    dispatch(requestVideoPlayerSeekToTs(ts));
  };

  const blocks = getComputedSegments({ plays, replays, skips, duration }).map(
    (block, i) => (
      <Segment
        key={`${block.type}${i}`}
        width={block.width}
        type={block.type}
        onClick={playVideoAt(block.start)}
      >
        {block.replayBlocks.map((block, i) => (
          <Segment
            key={`replay${i}`}
            width={block.width}
            type={block.type}
            onClick={playVideoAt(block.start)}
          />
        ))}
      </Segment>
    )
  );

  return <div className="viewer__timeline">{blocks}</div>;
};

export default ViewerInsightsList;
